import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { ROLE_PERMISSIONS, MODULE_PATHS, ModulePermissions } from '../lib/permissions';
import { UserRole } from '../redux/authSlice';

type Module = keyof ModulePermissions;

export const usePermissions = () => {
  const userRole = useSelector((state: RootState) => state.auth.role);
  
  const permissions = ROLE_PERMISSIONS[userRole] || ROLE_PERMISSIONS[UserRole.None];

  const getModulePermissions = (module: Module) => {
    return permissions[module];
  };

  const canView = (module: Module) => {
    return permissions[module]?.canView || false;
  };

  const canCreate = (module: Module) => {
    return permissions[module]?.canCreate || false;
  };

  const canEdit = (module: Module) => {
    return permissions[module]?.canEdit || false;
  };

  const canDelete = (module: Module) => {
    return permissions[module]?.canDelete || false;
  };

  const canApprove = (module: Module) => {
    return permissions[module]?.canApprove || false;
  };

  const canSync = (module: Module) => {
    return permissions[module]?.canSync || false;
  };

  const canUpdateStatus = (module: Module) => {
    return permissions[module]?.canUpdateStatus || false;
  };

  const canAccessPath = (pathname: string): boolean => {
    // Always allow dashboard access
    if (pathname === '/') return canView('dashboard');

    // Check each module's paths
    for (const [module, paths] of Object.entries(MODULE_PATHS)) {
      for (const path of paths) {
        // Convert glob pattern to regex
        const pattern = path.replace(/\*/g, '.*');
        const regex = new RegExp(`^${pattern}$`);
        
        if (regex.test(pathname)) {
          return canView(module as Module);
        }
      }
    }

    // ROLE_PERMISSIONS.Admin.screens.canView
    // Default deny
    return false;
  };

  const hasAnyPermission = () => {
    return Object.values(permissions).some(p => p.canView);
  };

  return {
    userRole,
    permissions,
    getModulePermissions,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canApprove,
    canAccessPath,
    hasAnyPermission,
    canSync,
    canUpdateStatus
  };
};
